<template>
<div>

    <div v-if="loading" class="text-center">
        <i class="fa fa-circle-o-notch fa-spin"></i> Loading
    </div>
    <div v-else>
        <vue-headful :title="'Inspection-'+inspectionDateFormatted + '-'+inspection.JobsiteName+' :: MeritEHS'" />

        <h4 class="py-3 mb-4 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                    <router-link :to="`/inspections`">
                        <i class="fa fa-list"></i> Inspections
                    </router-link>
                </li>
                <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">
                    <inspection-name :inspection="inspection" />
                </li>
            </ol>
        </h4>

        <div class="row mb-4">
            <div class="col-xl-8 col-lg-12 col-md-12">

                <div class="break-protect">
                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Inspection Details</strong>

                            <div class="float-sm-right float-none d-print-none d-block d-sm-inline-block mt-2 mt-sm-0">
                                <template v-if="canEditInsp">
                                  <router-link :to="`/inspections/edit/${inspection.InspectionId}`" class="btn btn-sm btn-light">
                                      <i class="fa fa-edit"></i> Edit
                                  </router-link>
                                </template>

                                <router-link :to="`/inspections/create?clone_id=${inspection.InspectionId}`" class="btn btn-sm btn-light" v-if="canClone">
                                    <i class="fa fa-copy"></i> Clone
                                </router-link>

                                <b-dropdown variant="light" size="sm">
                                    <template slot="button-content">
                                        <i class="fa fa-envelope"></i> Email
                                    </template>
                                    <b-dropdown-item :href="emailLink">
                                        Send Link
                                    </b-dropdown-item>
                                    <b-dropdown-item href="#">
                                        <router-link :to="`/inspections/email-pdf/${InspectionId}`">
                                            Send PDF
                                        </router-link>
                                    </b-dropdown-item>
                                </b-dropdown>

                                <b-dropdown variant="light" size="sm">
                                    <template slot="button-content">
                                        <i class="fa fa-file-pdf-o"></i> PDF
                                    </template>
                                    <b-dropdown-item href="#">
                                        <router-link :to="`/inspections/pdf/${InspectionId}`">
                                            PDF from HTML
                                        </router-link>
                                    </b-dropdown-item>
                                    <b-dropdown-item href="#">
                                        <router-link :to="`/inspections/build-pdf/${InspectionId}`">
                                            PDF Builder
                                        </router-link>
                                    </b-dropdown-item>
                                </b-dropdown>

                                <router-link :to="`/inspections/delete/${inspection.InspectionId}`" class="btn btn-sm btn-light" v-if="canEditInsp">
                                    <i class="fa fa-trash text-danger"></i> <span class="d-none d-sm-inline">Delete</span>
                                </router-link>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-6">
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Jobsite:</div>
                                        <div class="col-md-8 col-sm-12">
                                            <router-link :to="`/jobsites/view/${inspection.JobsiteId}`">{{ inspection.JobsiteNumber }} - {{ inspection.JobsiteName }}</router-link>
                                        </div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Jobsite#:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.JobsiteNumber }}</div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3" v-if="inspection.hide_superintendent != 1">
                                        <div class="col-md-4 col-sm-12 text-muted">Superintendent:</div>
                                        <div class="col-md-8 col-sm-12"><user-name :user="superintendent" /></div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted"># of Employees On Site:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.NumberOfEmployees }}</div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Division:</div>
                                        <div class="col-md-8 col-sm-12">
                                            <span v-if="inspection.division_code">{{ inspection.division_code }} - </span>{{ inspection.DivisionName }}
                                        </div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3" v-if="inspection.hide_equipment != 1">
                                        <div class="col-md-4 col-sm-12 text-muted">Equipment:</div>
                                        <div class="col-md-8 col-sm-12" v-if="inspection.equipment_id">
                                            <router-link :to="`/equipment/view/${inspection.equipment_id}`" title="View Equipment">
                                                {{ inspection.equipment_type_name }} - {{ inspection.model_number }} - {{ inspection.serial_number }}<template v-if="inspection.equipment_name"> - {{ inspection.equipment_name }}</template>
                                            </router-link>
                                            <template v-if="(inspection.current_tracking_status != 'Checked In' && inspection.current_tracking_status != null) && (this.$root.hasPermission(['equipment.manage', 'equipment.create']) || inspection.equipment_employee_id == this.$store.state.user.EmployeeId)">
                                                <router-link :to="`/equipment/tracking/${inspection.equipment_id}/edit`" class="btn btn-xs btn-light" title="Check In Equipment">
                                                    <i class="fa fa-edit"></i> Check In
                                                </router-link>
                                            </template>
                                        </div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Subcontractors Onsite:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.subcontractors_onsite }}</div>
                                    </div>
                                </div>
                                <div class="col-lg-6">
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Date of Inspection:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.InspectionDate | moment("MM/DD/YYYY") }}</div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Created Date:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Last Updated:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.updated_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                                    </div>
                                     <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Inspection Status:</div>
                                        <div class="col-md-8 col-sm-12">
                                            <h4 :class="`badge badge-${inspection.status_color}`">{{ inspection.status_name }}</h4>
                                        </div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Inspection Form:</div>
                                        <div class="col-md-8 col-sm-12">
                                            <router-link :to="`/inspections/forms/view/${inspection.inspection_form_id}`" title="View Inspection Form">
                                                {{ inspection.form_name }}
                                            </router-link>
                                        </div>
                                    </div>
                                     <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">Inspected By:</div>
                                        <div class="col-md-8 col-sm-12">
                                            <template v-if="inspection.hide_creator == 0 || $store.state.user.is_admin == true">
                                                <user-name :user="owner" v-if="inspection.CreatedByUserId" />
                                                <employee-name :employee="{EmployeeId: inspection.CreatedByEmployeeId, LastName: inspection.EmpLastName, FirstName: inspection.EmpFirstName, EmployeeNumber: inspection.EmpEmployeeNumber}" v-else-if="inspection.CreatedByEmployeeId" />
                                            </template>
                                        </div>
                                    </div>
                                    <div class="row mb-sm-2 mb-3">
                                        <div class="col-md-4 col-sm-12 text-muted">General Contractor:</div>
                                        <div class="col-md-8 col-sm-12">{{ inspection.GeneralContractor }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <template v-if="enable_insp_scmm == 1 && safety_committee_members">
                    <div class="break-protect card mb-4">
                        <div class="card-header">
                            <strong>Safety Committee</strong>
                        </div>
                        <div class="card-body">
                            <template v-if="safety_committee_members">
                                <label class="text-muted">Meeting Date:</label> {{ inspection.scmm_date | moment("MM/DD/YYYY") }}<br>
                                <label class="text-muted">Attendees:</label><br>
                                <div v-for="member in safety_committee_members" class="mt-1">
                                    {{ member.name }} | {{ member.craft }} | {{ member.company }}
                                </div>
                            </template>
                            <template v-else>
                                -- No Safety Committee Meeting Held --
                            </template>
                        </div>
                    </div>

                    <div class="break-protect card mb-4">
                        <div class="card-header">
                            <strong>Safety Committee Meeting Minutes</strong>
                        </div>
                        <div class="card-body">
                            <template v-if="safety_committee_members">
                                <div class="mb-4">
                                    <div class="text-muted">A. Discuss inspection results and status of project Corrective Actions:</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_status_of_actions" v-html="inspection.scmm_status_of_actions"></div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="text-muted">B. Project incidents and lessons learned:</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_lessons_learned" v-html="inspection.scmm_lessons_learned">
                                        </div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="text-muted">C. Safety concerns and suggestions voiced by any other project team member(s):</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_suggested_safety" v-html="inspection.scmm_suggested_safety">
                                        </div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="text-muted">D. Upcoming safety sensitive activities/tasks:</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_upcoming_activities" v-html="inspection.scmm_upcoming_activities">
                                        </div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="text-muted">E. Proactive safety measures being utilized throughout the project, and/or individuals who have displayed HSE leadership:</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_proactive_measures" v-html="inspection.scmm_proactive_measures">
                                        </div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                                <div class="mb-4">
                                    <div class="text-muted">F. Any other relevant topic discussed by the safety committee:</div>
                                    <div class="">
                                        <div v-if="inspection.scmm_other" v-html="inspection.scmm_other">
                                        </div>
                                        <div v-else>--</div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                -- No Safety Committee Meeting Held --
                            </template>
                        </div>
                    </div>
                </template>

                <div class="mb-3 break-protect" v-if="custom_field_sections && custom_field_sections.length > 0">
                    <incident-sections-view :sections="custom_field_sections"></incident-sections-view>
                </div>

                <div class="card mb-4">
                    <div class="card-header">
                        <strong>Additional Comments</strong>
                    </div>
                    <div class="card-body" style="white-space: pre;">{{ inspection.Comment }}</div>
                </div>

                <div class="card mb-4">
                    <div class="card-header">
                        <strong>Additional Files ({{ files.length }})</strong>
                    </div>
                    <div class="card-body">
                        <template v-if="files.length > 0">
                            <file-list :files="files" boxSize="col-xl-12 col-md-6 col-12" />
                        </template>
                        <template v-else>
                            -- No Files --
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-xl-4 col-lg-12">
                <div class="break-protect card mb-4">
                    <div class="card-header"><strong><i class="fa fa-files"></i> Summary</strong></div>
                    <ul class="list-group list-group-flush">
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                            <div class="text-muted">Total {{ labelCompliant }}</div>
                            <div>{{ inspection.TotalCompliant }} <span class="text-info" v-if="inspection.total_compliant_pts != inspection.TotalCompliant">({{ inspection.total_compliant_pts }} pts)</span></div>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                            <div class="text-muted">Total {{ labelNonCompliant }}</div>
                            <div>
                                {{ (inspection.TotalInspected - inspection.TotalCompliant) }}
                                <span class="text-info" v-if="(inspection.TotalInspected - inspection.TotalCompliant) != (inspection.total_inspected_pts - inspection.total_compliant_pts)">
                                    ({{ (inspection.total_inspected_pts - inspection.total_compliant_pts) }} pts)
                                </span>
                            </div>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                            <div class="text-muted">Total Inspected</div>
                            <div>{{ inspection.TotalInspected }} <span class="text-info" v-if="inspection.total_inspected_pts != inspection.TotalInspected">({{ inspection.total_inspected_pts }} pts)</span></div>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                            <div class="text-muted">Score</div>
                            <div>
                                <inspection-score :data="inspection" :displayType="'badge'" />
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="break-protect card mb-4">
                    <div class="card-header"><strong><i class="fa fa-building"></i> Jobsite</strong></div>
                    <div class="card-body">
                        <div class="row mb-1">
                            <div class="col-xl-3 text-xl-right text-muted mb-xl-0 mb-1">
                                Number:
                            </div>
                            <div class="col-xl-9">
                                <router-link :to="`/jobsites/view/${inspection.JobsiteId}`">{{ inspection.JobsiteNumber }}</router-link>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <div class="col-xl-3 text-xl-right text-muted mb-xl-0 mb-1">
                                Name:
                            </div>
                            <div class="col-xl-9">
                                <router-link :to="`/jobsites/view/${inspection.JobsiteId}`">{{ inspection.JobsiteName }}</router-link>
                            </div>
                        </div>
                        <div class="row mb-1">
                            <div class="col-xl-3 text-xl-right text-muted mb-xl-0 mb-1">
                                Division:
                            </div>
                            <div class="col-xl-9">
                                {{ inspection.division_code }}<span v-if="inspection.division_code">&nbsp;-&nbsp;</span>{{ inspection.DivisionName }}
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-xl-3 text-xl-right text-muted mb-xl-0 mb-1">
                                Address:
                            </div>
                            <div class="col-xl-9">
                                {{ inspection.Address }} {{ inspection.Address2 }}<br />
                                {{ inspection.City }}<span v-if="inspection.City && inspection.State">, </span>{{ inspection.State }} {{ inspection.Zip }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card" v-if="personnel.length > 0">
                    <div class="card-header">
                        <strong>Personnel</strong>
                    </div>
                    <div class="card-body">
                        <div class="row mb-2" v-for="(field, i) in personnel">
                            <div class="col-lg-3 col-md-4 col-sm-12 text-muted text-md-right">{{ field.field_label }}:</div>
                            <div class="col-lg-7 col-md-8 col-sm-12">
                                <router-link :to="`/employees/view/${field.employee_id}`" v-if="field.employee_id" title="View Employee">
                                    {{ field.EmployeeNumber }} - {{ field.employee_first_name }} {{ field.employee_last_name }}
                                </router-link>
                                <span v-else>{{ field.other_personnel_name }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <inspection-assessment
            :inspection="inspection"
            :sections="sections"
            :canEdit="canEditFiles"
            :result_labels="{compliant: labelCompliant, noncompliant: labelNonCompliant, na: labelNA}" />
    </div>
</div>
</template>

<script>
import moment from 'moment-timezone';

export default {
    data() {
        return {
            loading: true,
            InspectionId: this.$route.params.id,
            userTimezone: this.$store.state.user.timezone,
            inspection: {},
            sections: [],
            custom_field_sections: [],
            files: [],
            safety_committee_members: [],
            emailLink: '',
            result_labels: null,
            personnel: [],
            enable_insp_scmm: null,
        }
    },

    computed: {
        owner() {
            return {
                FirstName: this.inspection.owner_first_name,
                LastName: this.inspection.owner_last_name,
                deleted_at: this.inspection.owner_deleted_at,
            };
        },

        superintendent() {
            return {
                FirstName: this.inspection.super_first_name,
                LastName: this.inspection.super_last_name,
                deleted_at: this.inspection.super_deleted_at,
            };
        },

        inspectionDateFormatted(){
            //Format the date
            return moment(this.inspection.InspectionDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
        },

        canEditInsp(){
            //Check manager permission
            if(this.$root.hasPermission('inspections.manage'))
            {
                return true;
            }

            //Check manager permission
            if(this.$root.hasPermission('inspections.create') && this.inspection.CreatedByUserId == this.$store.state.user.user_id)
            {
                return true;
            }

            //Default
            return false;
        },

        canEditFiles(){
            //Check the user ID against the record id
            if(this.inspection.UserId == this.$store.state.user.UserId)
            {
                return true;
            }

            //Check manager permission
            if(this.$root.hasPermission('inspections.manage'))
            {
                return true;
            }

            //Default
            return false;
        },

        canClone(){
            //Check manager permission
            if(this.$root.hasPermission(['inspections.create', 'inspections.manage']))
            {
                return true;
            }
        },

        labelCompliant(){
            //Check if set
            if(this.result_labels && this.result_labels.compliant)
            {
                return this.result_labels.compliant;
            }

            //Default
            return 'Compliant';
        },

        labelNonCompliant(){
            //Check if set
            if(this.result_labels && this.result_labels.noncompliant)
            {
                return this.result_labels.noncompliant;
            }

            //Default
            return 'Non-Compliant';
        },

        labelNA(){
            //Check if set
            if(this.result_labels && this.result_labels.na)
            {
                return this.result_labels.na;
            }

            //Default
            return 'N/A';
        },
    },

    methods: {

        inspectionPartsUpdated(data) {
            //Update the cost
            this.inspection.cost = data.inspection_cost;

            //Redraw the table
            this.$refs.inspectionParts.refreshData();
        },

        getInspection() {
            //Get the data
            this.$http
                .get('inspections/view/' + this.InspectionId)
                .then(response => {
                    //Set all the data
                    this.inspection = response.data.inspection;
                    this.sections = response.data.sections;
                    this.safety_committee_members = JSON.parse(this.inspection['safety_committee_members']);
                    this.emailLink = response.data.emailLink;
                    this.files = response.data.files;
                    this.custom_field_sections = response.data.custom_field_sections;
                    this.personnel = response.data.personnel;
                    this.enable_insp_scmm = response.data.enable_insp_scmm;

                    //Check for Result Labels
                    if(this.inspection.result_labels)
                    {
                        this.result_labels = JSON.parse(this.inspection.result_labels);
                    }

                    //Set loading
                    this.loading = false;
                })
                .catch(response => {

                });
        },

    },
    mounted() {
        //Get the inspections
        this.getInspection();
    }
}
</script>
<style scoped>
.break-protect {
    page-break-inside: avoid;
}
</style>